import { openAPIClient } from '@/services/httpServices/openApiClient';

import type {
  CreateSpaceSettingsResponse,
  CreateSpaceSettingsServiceConfig,
  DeleteSpaceSettingsServiceConfig,
  GetSpaceCodeServiceConfig,
  GetSpaceSettingsServiceConfig,
  SpaceCodeResponse,
  SpaceSettingsResponse,
  SpacesListResponse,
  SpacesListServiceConfig,
  UpdateSpaceSettingsResponse,
  UpdateSpaceSettingsServiceConfig,
} from './types';

/**
 * Gets a paginated list of spaces
 * Used in admin panels for displaying all spaces
 */
export async function getSpacesListService({
  httpRequestConfig,
  params,
}: SpacesListServiceConfig): Promise<SpacesListResponse> {
  const client = await openAPIClient(httpRequestConfig);
  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/admin/spaces', {
    params: {
      query: params.query,
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Gets settings for a specific space
 * Used for viewing and managing space configuration
 */
export async function getSpaceSettingsService({
  httpRequestConfig,
  params,
}: GetSpaceSettingsServiceConfig): Promise<SpaceSettingsResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/spaceSettings/{spaceId}', {
    params: {
      path: {
        spaceId: params.path.spaceId,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Gets the training login code for a specific space
 * Used for retrieving access codes for trainer app login
 */
export async function getSpaceCodeService({
  httpRequestConfig,
  params,
}: GetSpaceCodeServiceConfig): Promise<SpaceCodeResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.GET('/v1/auth/spaceSettings/{spaceId}/code', {
    params: {
      path: {
        spaceId: params.spaceId,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Creates a new space with initial settings
 * Used in space creation workflows
 */
export async function createSpaceSettingsService({
  httpRequestConfig,
  params,
}: CreateSpaceSettingsServiceConfig): Promise<CreateSpaceSettingsResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.POST('/v1/auth/spaceSettings', {
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  if (response.data === undefined) {
    throw new Error('No data returned from create space settings');
  }

  return response.data;
}

/**
 * Updates settings for an existing space
 * Used in space settings editing forms
 */
export async function updateSpaceSettingsService({
  httpRequestConfig,
  params,
}: UpdateSpaceSettingsServiceConfig): Promise<UpdateSpaceSettingsResponse> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.PATCH('/v1/auth/spaceSettings/{spaceId}', {
    params: {
      path: {
        spaceId: params.path.spaceId,
      },
    },
    body: params.body,
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }

  return response.data;
}

/**
 * Deletes a space and all its associated data
 * Used in space deletion confirmations
 */
export async function deleteSpaceSettingsService({
  httpRequestConfig,
  params,
}: DeleteSpaceSettingsServiceConfig): Promise<void> {
  const client = await openAPIClient(httpRequestConfig);

  if (params === undefined) {
    throw new Error('Params are required');
  }

  const response = await client.DELETE('/v1/auth/spaceSettings/{spaceId}', {
    params: {
      path: {
        spaceId: params.path.spaceId,
      },
    },
  });

  if (response.error !== undefined) {
    throw new Error(response.error.message);
  }
}
